/**
 * Web Mercator tile addressing — the "slippy map" scheme every free tile source speaks.
 *
 * A tile is `(z, x, y)`: zoom `z` splits the world into `2^z × 2^z` tiles, `x` runs east from
 * 180° W and `y` runs **south** from about 85.05° N. The projection is spherical Mercator
 * (EPSG:3857), which — importantly — is *not* the WGS84 ellipsoid the rest of the engine uses.
 * Every tile source in this project publishes on the spherical convention, so tile corners are
 * converted with the spherical formulas here and then handed to the geodesy module as plain
 * latitude/longitude. Mixing the two up puts imagery about 20 km off the terrain at mid
 * latitudes, which is the kind of bug that looks like "the DEM is wrong".
 *
 * Everything here is a pure function on numbers. No allocation, nothing to dispose.
 */

import { clamp } from '../../core/math.js';
import { EARTH_MEAN_RADIUS_M } from '../geo.js';

/**
 * The latitude where the Mercator projection is truncated to make the world square:
 * `atan(sinh(π))`. Tiles do not exist beyond it, which is why slippy maps have no poles.
 */
export const MERCATOR_MAX_LATITUDE_DEG = 85.0511287798066;

/** Equatorial circumference on the spherical Mercator sphere [m]. */
export const MERCATOR_CIRCUMFERENCE_M = 2 * Math.PI * 6_378_137;

/** Highest zoom any provider in this project serves. Bounds the key packing below. */
export const MAX_TILE_ZOOM = 20;

/** Tiles across the world at this zoom. */
export function tilesPerAxis(zoom: number): number {
  return 1 << zoom;
}

// --- longitude / latitude ↔ tile coordinates -------------------------------------------------

/** Fractional tile x for a longitude. */
export function longitudeToTileX(longitudeDeg: number, zoom: number): number {
  return ((longitudeDeg + 180) / 360) * tilesPerAxis(zoom);
}

/**
 * Fractional tile y for a latitude. Clamped to the Mercator cut-off, and to `[0, 2^z]`.
 *
 * The second clamp is not belt-and-braces. `MERCATOR_MAX_LATITUDE_DEG` is `atan(sinh(π))`
 * rounded to a double, so pushing it back through `asinh(tan(·))` does not land exactly on π
 * and the result comes out at −5.7 × 10⁻¹³ instead of 0. Any caller that floors that gets tile
 * row −1, which is a tile that does not exist, and the failure shows up as a missing strip at
 * the top of the world rather than as anything resembling a rounding error.
 */
export function latitudeToTileY(latitudeDeg: number, zoom: number): number {
  const lat = clamp(latitudeDeg, -MERCATOR_MAX_LATITUDE_DEG, MERCATOR_MAX_LATITUDE_DEG);
  const latRad = (lat * Math.PI) / 180;
  const n = tilesPerAxis(zoom);
  const y = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n;
  return clamp(y, 0, n);
}

/** Longitude of a (possibly fractional) tile x — the tile's western edge at integer x. */
export function tileXToLongitude(x: number, zoom: number): number {
  return (x / tilesPerAxis(zoom)) * 360 - 180;
}

/** Latitude of a (possibly fractional) tile y — the tile's northern edge at integer y. */
export function tileYToLatitude(y: number, zoom: number): number {
  const n = Math.PI * (1 - (2 * y) / tilesPerAxis(zoom));
  return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
}

/** Integer tile x containing this longitude, wrapped into range. */
export function longitudeToTileXIndex(longitudeDeg: number, zoom: number): number {
  return wrapTileX(Math.floor(longitudeToTileX(longitudeDeg, zoom)), zoom);
}

/** Integer tile y containing this latitude, clamped into range. */
export function latitudeToTileYIndex(latitudeDeg: number, zoom: number): number {
  const n = tilesPerAxis(zoom);
  return clamp(Math.floor(latitudeToTileY(latitudeDeg, zoom)), 0, n - 1);
}

/**
 * Wrap a tile x index around the antimeridian.
 *
 * Longitude is cyclic and tile y is not, which is why only x gets this treatment: fly west out
 * of tile 0 and the next tile is `2^z − 1`, but fly north out of tile row 0 and there is simply
 * nothing there.
 */
export function wrapTileX(x: number, zoom: number): number {
  const n = tilesPerAxis(zoom);
  return ((x % n) + n) % n;
}

// --- keys --------------------------------------------------------------------------------------

/**
 * Pack `(z, x, y)` into one number for use as a Map key.
 *
 * A number rather than a `${z}/${x}/${y}` string on purpose: tile keys are built and compared
 * thousands of times per frame during quadtree selection, and a string key means an allocation
 * and a hash per lookup — precisely the steady drip non-negotiable #3 forbids. Zoom is capped
 * at {@link MAX_TILE_ZOOM}, so x and y each fit in 20 bits and the whole key fits in 45 bits,
 * comfortably inside the 53 an IEEE double represents exactly.
 */
export function tileKey(zoom: number, x: number, y: number): number {
  return (zoom * 0x100000 + x) * 0x100000 + y;
}

export function keyZoom(key: number): number {
  return Math.floor(key / (0x100000 * 0x100000));
}

export function keyX(key: number): number {
  return Math.floor(key / 0x100000) % 0x100000;
}

export function keyY(key: number): number {
  return key % 0x100000;
}

/**
 * Which elevation tile a terrain tile reads, and which part of it.
 *
 * Elevation and imagery run out at different depths — terrarium stops at z15, NAIP is a 1 m
 * source good to z17 — and the quadtree follows the *imagery*. Past the elevation cap a tile
 * therefore reads its nearest published ancestor and takes a sub-rectangle: at two levels down,
 * sixteen tiles share one raster and each reads a quarter-by-quarter window.
 *
 * All of it is integer shifts, deliberately. The window could be derived from the two tiles'
 * geographic bounds instead, but that is a subtraction of two nearly-equal degrees at z17 and it
 * would put a rounding error into a texture coordinate — the failure mode being terrain that is
 * *plausible* and in the wrong place, which no range check catches.
 *
 * Mutates and returns `out` so the streaming path allocates nothing.
 */
export function demWindow(
  zoom: number,
  x: number,
  y: number,
  elevationMaxZoom: number,
  out: DemWindow,
): DemWindow {
  const levelsUp = Math.max(0, zoom - elevationMaxZoom);
  const span = 1 / (1 << levelsUp);
  out.zoom = zoom - levelsUp;
  out.x = x >> levelsUp;
  out.y = y >> levelsUp;
  out.u0 = (x - (out.x << levelsUp)) * span;
  out.v0 = (y - (out.y << levelsUp)) * span;
  out.span = span;
  return out;
}

/** The elevation tile and window {@link demWindow} resolves to. */
export interface DemWindow {
  zoom: number;
  x: number;
  y: number;
  /** Normalised window into that tile's raster, 0…1. */
  u0: number;
  v0: number;
  span: number;
}

export function makeDemWindow(): DemWindow {
  return { zoom: 0, x: 0, y: 0, u0: 0, v0: 0, span: 1 };
}

/** Human-readable `z/x/y`, for logs and the debug overlay only. */
export function formatKey(key: number): string {
  return `${keyZoom(key)}/${keyX(key)}/${keyY(key)}`;
}

// --- geometry ------------------------------------------------------------------------------------

/** Geographic extent of a tile [deg]. */
export interface TileBounds {
  westDeg: number;
  southDeg: number;
  eastDeg: number;
  northDeg: number;
}

export function makeTileBounds(): TileBounds {
  return { westDeg: 0, southDeg: 0, eastDeg: 0, northDeg: 0 };
}

/** Fill `out` with a tile's geographic extent. Allocation-free. */
export function tileBounds(zoom: number, x: number, y: number, out: TileBounds): TileBounds {
  out.westDeg = tileXToLongitude(x, zoom);
  out.eastDeg = tileXToLongitude(x + 1, zoom);
  out.northDeg = tileYToLatitude(y, zoom);
  out.southDeg = tileYToLatitude(y + 1, zoom);
  return out;
}

/**
 * Ground resolution of one tile pixel at this zoom and latitude [m/px].
 *
 * The `cos(latitude)` is the whole story of Mercator: a z14 tile is about 2.4 m/px at the
 * equator and 1.2 m/px at 60° N. It is why the LOD selector works in metres rather than zoom
 * levels — the same zoom means very different detail depending on where you are flying.
 */
export function tileResolutionMPerPx(zoom: number, latitudeDeg: number, tileSizePx = 256): number {
  const latRad = (latitudeDeg * Math.PI) / 180;
  return (MERCATOR_CIRCUMFERENCE_M * Math.cos(latRad)) / (tilesPerAxis(zoom) * tileSizePx);
}

/** Ground width of a whole tile at this zoom and latitude [m]. */
export function tileSpanM(zoom: number, latitudeDeg: number): number {
  return tileResolutionMPerPx(zoom, latitudeDeg, 1);
}

/**
 * The zoom whose tiles are closest to `targetSpanM` wide at this latitude.
 * Used to pick the root level for quadtree traversal and to sanity-check LOD choices.
 */
export function zoomForTileSpan(targetSpanM: number, latitudeDeg: number): number {
  const latRad = (latitudeDeg * Math.PI) / 180;
  const z = Math.log2((MERCATOR_CIRCUMFERENCE_M * Math.cos(latRad)) / Math.max(1, targetSpanM));
  return clamp(Math.round(z), 0, MAX_TILE_ZOOM);
}

/**
 * Great-circle-ish distance from a point to a tile's extent [m], zero when inside.
 *
 * Cheap and slightly conservative: it measures the shortest distance to the bounding box in
 * degrees and scales it into metres, which over the ~100 km a tile can span is close enough to
 * drive LOD selection and never *under*-estimates enough to pop a tile out early.
 */
export function distanceToTileBoundsM(
  latitudeDeg: number,
  longitudeDeg: number,
  bounds: TileBounds,
): number {
  const dLat =
    latitudeDeg < bounds.southDeg
      ? bounds.southDeg - latitudeDeg
      : latitudeDeg > bounds.northDeg
        ? latitudeDeg - bounds.northDeg
        : 0;

  let dLon = 0;
  if (longitudeDeg < bounds.westDeg) dLon = bounds.westDeg - longitudeDeg;
  else if (longitudeDeg > bounds.eastDeg) dLon = longitudeDeg - bounds.eastDeg;
  if (dLon > 180) dLon = 360 - dLon; // the short way round the antimeridian

  const metresPerDegree = (Math.PI / 180) * EARTH_MEAN_RADIUS_M;
  const clampedLat = clamp(latitudeDeg, bounds.southDeg, bounds.northDeg);
  const northM = dLat * metresPerDegree;
  const eastM = dLon * metresPerDegree * Math.cos((clampedLat * Math.PI) / 180);
  return Math.hypot(northM, eastM);
}
